import { useState, useEffect } from 'react';
import { customersAPI, emailAPI, balanceAPI } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { 
  Users, 
  Search,
  Plus,
  Mail,
  Euro,
  Eye,
  Edit,
  ChevronLeft,
  Key,
  Copy,
  Check,
  History,
  TrendingUp,
  TrendingDown,
  Trash2,
  AlertTriangle,
  Loader2,
  Send,
  Clock,
  KeyRound,
  CreditCard
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CopyField = ({ label, value, testId }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} kopiert`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Kopieren fehlgeschlagen'); }
  };
  return (
    <div>
      <p className="font-manrope text-xs sm:text-sm text-gray-500">{label}</p>
      <div className="flex items-center gap-2">
        <p className="font-manrope text-sm sm:text-base flex-1 break-all" data-testid={testId}>{value}</p>
        <button
          onClick={handleCopy}
          className={`p-1.5 rounded-md border flex-shrink-0 transition-colors ${copied ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`}
          data-testid={`copy-${testId}`}
          title={`${label} kopieren`}
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
        </button>
      </div>
    </div>
  );
};

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceDescription, setBalanceDescription] = useState('');
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    internal_note: '',
    birthday: '',
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    password: '',
    internal_note: '',
    birthday: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (search = '') => {
    try {
      const response = await customersAPI.getAll(search);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(searchQuery);
  };

  const handleCreateCustomer = async () => {
    try {
      await customersAPI.create(formData);
      toast.success('Kunde erfolgreich erstellt');
      setShowCreateDialog(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        street: '',
        postal_code: '',
        city: '',
        password: '',
        birthday: '',
      });
      fetchCustomers();
    } catch (error) {
      console.error('Failed to create customer:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Kunden');
    }
  };

  const handleSendCustomerNumber = async (customer) => {
    try {
      await emailAPI.sendCustomerNumber(customer.id, 'customer_number');
      toast.success('E-Mail mit Kundennummer gesendet');
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Fehler beim Senden der E-Mail');
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedCustomer || !balanceAmount) return;
    
    try {
      await balanceAPI.update(
        selectedCustomer.id, 
        parseFloat(balanceAmount), 
        balanceDescription || 'Manuelle Anpassung'
      );
      toast.success('Guthaben aktualisiert');
      setShowBalanceDialog(false);
      setBalanceAmount('');
      setBalanceDescription('');
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update balance:', error);
      toast.error('Fehler beim Aktualisieren des Guthabens');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedCustomer) return;
    
    try {
      // Use the API client which handles cookies and CSRF automatically
      const response = await api.post(`/admin/customers/${selectedCustomer.id}/reset-password`);
      
      // Check if response contains the expected data
      if (response.data.email_sent) {
        toast.success(`Reset-Link an ${selectedCustomer.email} gesendet`);
        setSelectedCustomer(null);
      } else if (response.data.new_password) {
        setNewPassword(response.data.new_password);
        setShowPasswordDialog(true);
        toast.success('Neues Passwort generiert');
      } else {
        toast.success(response.data.message || 'Passwort zurückgesetzt');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      toast.error(errorMsg);
    }
  };

  const handleSendPaymentReminder = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await api.post(`/admin/customers/${selectedCustomer.id}/send-payment-reminder`);
      toast.success(response.data.message || 'Zahlungserinnerung gesendet');
    } catch (error) {
      console.error('Failed to send payment reminder:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden der Zahlungserinnerung';
      toast.error(errorMsg);
    }
  };

  const handleSendCancellationEmail = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await api.post(`/admin/customers/${selectedCustomer.id}/send-appointment-cancelled`);
      toast.success(response.data.message || 'Stornierungsmail gesendet');
    } catch (error) {
      console.error('Failed to send cancellation email:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden der Stornierungsmail';
      toast.error(errorMsg);
    }
  };

  const handleSendReviewRequest = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await api.post(`/admin/customers/${selectedCustomer.id}/send-review-request`);
      toast.success(response.data.message || 'Bewertungsanfrage gesendet');
    } catch (error) {
      console.error('Failed to send review request:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden der Bewertungsanfrage';
      toast.error(errorMsg);
    }
  };

  const [sendingQuickEmail, setSendingQuickEmail] = useState('');
  const [generatingCard, setGeneratingCard] = useState(false);

  const handleSendQuickEmail = async (templateType) => {
    if (!selectedCustomer) return;
    setSendingQuickEmail(templateType);
    try {
      await api.post(`/admin/customers/${selectedCustomer.id}/send-quick-email`, { template_type: templateType });
      toast.success('E-Mail erfolgreich gesendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-Mail konnte nicht gesendet werden');
    } finally {
      setSendingQuickEmail('');
    }
  };

  const handleGenerateCustomerCard = async () => {
    if (!selectedCustomer) return;
    setGeneratingCard(true);
    try {
      const res = await api.post(`/admin/customers/${selectedCustomer.id}/generate-card`);
      const pdf = res.data.pdf_base64;
      const blob = new Blob([Uint8Array.from(atob(pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kundenkarte_${res.data.customer_number || selectedCustomer.customer_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Kundenkarte erstellt');
    } catch (err) {
      toast.error('Fehler beim Erstellen der Kundenkarte');
    } finally {
      setGeneratingCard(false);
    }
  };

  const openEditDialog = () => {
    if (!selectedCustomer) return;
    setEditFormData({
      first_name: selectedCustomer.first_name || '',
      last_name: selectedCustomer.last_name || '',
      email: selectedCustomer.email || '',
      phone: selectedCustomer.phone || '',
      street: selectedCustomer.street || '',
      postal_code: selectedCustomer.postal_code || '',
      city: selectedCustomer.city || '',
      internal_note: selectedCustomer.internal_note || '',
      birthday: selectedCustomer.birthday || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await api.put(`/admin/customers/${selectedCustomer.id}`, editFormData);
      toast.success('Kundendaten aktualisiert');
      setShowEditDialog(false);
      // Update local state
      setSelectedCustomer(response.data);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update customer:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Aktualisieren der Kundendaten';
      toast.error(errorMsg);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  const handleViewHistory = async (customer) => {
    try {
      const response = await balanceAPI.getHistoryByCustomer(customer.id);
      setBalanceHistory(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      console.error('Failed to fetch balance history:', error);
      toast.error('Fehler beim Laden der Zahlungshistorie');
    }
  };

  const handleDeleteCustomer = async () => {
    if (deleteConfirmText !== 'LÖSCHEN' || !customerToDelete) {
      toast.error('Bitte geben Sie LÖSCHEN ein, um zu bestätigen');
      return;
    }

    try {
      await api.delete(`/admin/customers/${customerToDelete.id}?confirm=DELETE`);
      toast.success(`Kunde ${customerToDelete.first_name} ${customerToDelete.last_name} wurde permanent gelöscht`);
      setShowDeleteDialog(false);
      setCustomerToDelete(null);
      setDeleteConfirmText('');
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Kunden');
    }
  };

  const openDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteConfirmText('');
    setShowDeleteDialog(true);
  };

  const getBalanceColor = (balance) => {
    if (balance < 0) return 'text-red-600';
    if (balance > 0) return 'text-emerald-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-customers">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-[#1A1A1A] mb-2">
            Kundenverwaltung
          </h1>
          <p className="font-manrope text-gray-600">
            {customers.length} Kunden insgesamt
          </p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Kunde
        </Button>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Suche nach Kundennummer, Name, E-Mail oder Telefon..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="search-btn">
              Suchen
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Customers Table - Desktop */}
      <Card className="hidden md:block" data-testid="customers-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Kundennr.</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Name</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">E-Mail</th>
                  <th className="text-left py-4 px-6 font-barlow font-bold text-sm text-gray-500">Telefon</th>
                  <th className="text-right py-4 px-6 font-barlow font-bold text-sm text-gray-500">Saldo</th>
                  <th className="text-right py-4 px-6 font-barlow font-bold text-sm text-gray-500">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr 
                    key={customer.id} 
                    className="border-b last:border-0 table-row-hover"
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="py-4 px-6">
                      <span className="font-barlow font-bold text-[#9B1B30]">
                        {customer.customer_number}
                      </span>
                    </td>
                    <td className="py-4 px-6 font-manrope">
                      {customer.first_name} {customer.last_name}
                    </td>
                    <td className="py-4 px-6 font-manrope text-sm text-gray-600">
                      {customer.email}
                    </td>
                    <td className="py-4 px-6 font-manrope text-sm text-gray-600">
                      {customer.phone}
                    </td>
                    <td className={`py-4 px-6 font-barlow font-bold text-right ${getBalanceColor(customer.balance)}`}>
                      {customer.balance.toFixed(2).replace('.', ',')} €
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedCustomer(customer)}
                          data-testid={`view-customer-${customer.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowBalanceDialog(true);
                          }}
                          data-testid={`edit-balance-${customer.id}`}
                        >
                          <Euro className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSendCustomerNumber(customer)}
                          data-testid={`send-email-${customer.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {customers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-manrope text-gray-500">Keine Kunden gefunden</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customers Cards - Mobile */}
      <div className="md:hidden space-y-3" data-testid="customers-mobile">
        {customers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-manrope text-gray-500">Keine Kunden gefunden</p>
            </CardContent>
          </Card>
        ) : (
          customers.map((customer) => (
            <Card 
              key={customer.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedCustomer(customer)}
              data-testid={`customer-card-${customer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="font-barlow font-bold text-[#9B1B30] text-sm">
                      {customer.customer_number}
                    </span>
                    <h3 className="font-manrope font-semibold text-base mt-0.5">
                      {customer.first_name} {customer.last_name}
                    </h3>
                  </div>
                  <span className={`font-barlow font-bold text-lg ${getBalanceColor(customer.balance)}`}>
                    {customer.balance.toFixed(2).replace('.', ',')} €
                  </span>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p className="font-manrope truncate">{customer.email}</p>
                  <p className="font-manrope">{customer.phone}</p>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCustomer(customer);
                      setShowBalanceDialog(true);
                    }}
                  >
                    <Euro className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendCustomerNumber(customer);
                    }}
                  >
                    <Mail className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCustomer(customer);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Customer Detail Sheet - Mobile Optimized */}
      <Sheet open={!!selectedCustomer && !showBalanceDialog && !showHistoryDialog} onOpenChange={() => setSelectedCustomer(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-barlow text-lg sm:text-xl">
              Kundendetails
            </SheetTitle>
          </SheetHeader>
          {selectedCustomer && (
            <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6 pb-6">
              {/* Customer Header */}
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#9B1B30] text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-barlow font-bold text-lg sm:text-2xl">
                    {selectedCustomer.first_name[0]}{selectedCustomer.last_name[0]}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-barlow font-bold text-base sm:text-xl truncate">
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </p>
                  <p className="font-manrope text-[#9B1B30] font-medium text-sm sm:text-base">
                    {selectedCustomer.customer_number}
                  </p>
                </div>
              </div>

              {/* Customer Info Grid - Responsive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="col-span-1 sm:col-span-2">
                  <CopyField label="E-Mail" value={selectedCustomer.email} testId="customer-email" />
                </div>
                <CopyField label="Telefon" value={selectedCustomer.phone} testId="customer-phone" />
                <div>
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Kunde seit</p>
                  <p className="font-manrope text-sm sm:text-base">
                    {format(new Date(selectedCustomer.created_at), 'dd.MM.yyyy')}
                  </p>
                </div>
                {selectedCustomer.birthday && (
                  <div>
                    <p className="font-manrope text-xs sm:text-sm text-gray-500">Geburtstag</p>
                    <p className="font-manrope text-sm sm:text-base" data-testid="customer-birthday">
                      {(() => {
                        const parts = selectedCustomer.birthday.split('-');
                        const months = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
                        if (parts.length === 2) return `${parseInt(parts[0])}. ${months[parseInt(parts[1])-1] || ''}`;
                        return selectedCustomer.birthday;
                      })()}
                    </p>
                  </div>
                )}
                <div className="col-span-1 sm:col-span-2">
                  <CopyField 
                    label="Adresse" 
                    value={[selectedCustomer.street, `${selectedCustomer.postal_code || ''} ${selectedCustomer.city || ''}`.trim()].filter(Boolean).join(', ')} 
                    testId="customer-address" 
                  />
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <CopyField label="Kundennummer" value={selectedCustomer.customer_number} testId="customer-number" />
                </div>
                <div className="col-span-1 sm:col-span-2 bg-gray-50 rounded-lg p-3">
                  <p className="font-manrope text-xs sm:text-sm text-gray-500">Kontostand</p>
                  <p className={`font-barlow font-bold text-xl sm:text-2xl ${getBalanceColor(selectedCustomer.balance)}`}>
                    {selectedCustomer.balance.toFixed(2).replace('.', ',')} €
                  </p>
                </div>
                {selectedCustomer.internal_note && (
                  <div className="col-span-1 sm:col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="font-manrope text-xs sm:text-sm text-yellow-700 font-medium mb-1">Interne Notiz</p>
                    <p className="font-manrope text-sm text-yellow-900 whitespace-pre-wrap">{selectedCustomer.internal_note}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons - Stacked on Mobile */}
              <div className="space-y-2 sm:space-y-3">
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    const c = selectedCustomer;
                    const text = [
                      `${c.first_name} ${c.last_name}`,
                      c.customer_number,
                      c.email,
                      c.phone,
                      [c.street, `${c.postal_code || ''} ${c.city || ''}`.trim()].filter(Boolean).join(', ')
                    ].filter(Boolean).join('\n');
                    try {
                      await navigator.clipboard.writeText(text);
                      toast.success('Alle Kundendaten kopiert');
                    } catch { toast.error('Kopieren fehlgeschlagen'); }
                  }}
                  data-testid="copy-all-customer-data-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  <span className="text-sm">Alle Daten kopieren</span>
                </Button>
                <Button 
                  className="btn-primary w-full"
                  onClick={() => handleSendCustomerNumber(selectedCustomer)}
                  data-testid="send-customer-number-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="text-sm">Kundennummer senden</span>
                </Button>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowBalanceDialog(true)}
                    data-testid="edit-balance-btn"
                  >
                    <Euro className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="text-xs sm:text-sm">Guthaben</span>
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => handleViewHistory(selectedCustomer)}
                    data-testid="view-history-btn"
                  >
                    <History className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="text-xs sm:text-sm">Historie</span>
                  </Button>
                </div>
                
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={handleResetPassword}
                  data-testid="reset-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  <span className="text-sm">Neues Passwort senden</span>
                </Button>

                {/* New: Edit Customer Button */}
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={openEditDialog}
                  data-testid="edit-customer-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  <span className="text-sm">Kundendaten bearbeiten</span>
                </Button>

                {/* New: Payment Reminder Button - only show if balance is negative */}
                {selectedCustomer.balance < 0 && (
                  <Button 
                    variant="outline"
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={handleSendPaymentReminder}
                    data-testid="send-payment-reminder-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    <span className="text-sm">Zahlungserinnerung senden</span>
                  </Button>
                )}

                {/* New: Appointment Cancelled Button */}
                <Button 
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  onClick={handleSendCancellationEmail}
                  data-testid="send-cancellation-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="text-sm">Termin storniert per Mail senden</span>
                </Button>

                {/* Review Request Button */}
                <Button 
                  className="w-full bg-[#1e3a5f] hover:bg-[#152a45] text-white rounded-md"
                  onClick={handleSendReviewRequest}
                  data-testid="send-review-request-btn"
                >
                  <span className="text-sm">⭐ Kunde um Bewertung bitten</span>
                </Button>

              {/* Kundenkarte generieren */}
              <div className="pt-3 border-t">
                <Button
                  variant="outline"
                  className="w-full border-[#9B1B30] text-[#9B1B30] hover:bg-[#9B1B30]/5"
                  onClick={handleGenerateCustomerCard}
                  disabled={generatingCard}
                  data-testid="generate-customer-card-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {generatingCard ? 'Wird erstellt...' : 'Kundenkarte erstellen'}
                </Button>
              </div>

                {/* Quick Email Section */}
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-blue-800 flex items-center gap-2" data-testid="quick-email-section">
                    <Send className="w-4 h-4" />
                    Schnell-E-Mail an Kunden
                  </p>
                  
                  {/* Baustelle fertig */}
                  <p className="text-xs text-blue-700 font-medium mt-2">Baustelle fertig in:</p>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendQuickEmail('fertig_30min')} disabled={!!sendingQuickEmail} data-testid="quick-email-fertig-30min">
                      {sendingQuickEmail === 'fertig_30min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} 30 Min
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendQuickEmail('fertig_1stunde')} disabled={!!sendingQuickEmail} data-testid="quick-email-fertig-1stunde">
                      {sendingQuickEmail === 'fertig_1stunde' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} 1 Std
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendQuickEmail('fertig_morgen')} disabled={!!sendingQuickEmail} data-testid="quick-email-fertig-morgen">
                      {sendingQuickEmail === 'fertig_morgen' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Morgen
                    </Button>
                  </div>

                  {/* Schlüssel */}
                  <p className="text-xs text-blue-700 font-medium mt-2">Schlüssel:</p>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendQuickEmail('schluessel_erhalten')} disabled={!!sendingQuickEmail} data-testid="quick-email-schluessel-erhalten">
                      {sendingQuickEmail === 'schluessel_erhalten' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Erhalten
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleSendQuickEmail('schluessel_zurueck')} disabled={!!sendingQuickEmail} data-testid="quick-email-schluessel-zurueck">
                      {sendingQuickEmail === 'schluessel_zurueck' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />} Zurückgegeben
                    </Button>
                  </div>

                  {/* Verspätung */}
                  <p className="text-xs text-blue-700 font-medium mt-2">Verspätung:</p>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                      onClick={() => handleSendQuickEmail('verspaetung_10min')} disabled={!!sendingQuickEmail} data-testid="quick-email-verspaetung-10">
                      {sendingQuickEmail === 'verspaetung_10min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 10 Min
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                      onClick={() => handleSendQuickEmail('verspaetung_20min')} disabled={!!sendingQuickEmail} data-testid="quick-email-verspaetung-20">
                      {sendingQuickEmail === 'verspaetung_20min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 20 Min
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                      onClick={() => handleSendQuickEmail('verspaetung_30min')} disabled={!!sendingQuickEmail} data-testid="quick-email-verspaetung-30">
                      {sendingQuickEmail === 'verspaetung_30min' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Clock className="w-3 h-3 mr-1" />} 30 Min
                    </Button>
                  </div>
                </div>
              </div>

              {/* Delete Button */}
              <div className="pt-3 sm:pt-4 border-t">
                <Button 
                  variant="destructive"
                  className="w-full"
                  onClick={() => openDeleteDialog(selectedCustomer)}
                  data-testid="delete-customer-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  <span className="text-sm">Kunde löschen</span>
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Kunde permanent löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Sie sind dabei, <strong>{customerToDelete?.first_name} {customerToDelete?.last_name}</strong> ({customerToDelete?.customer_number}) <span className="text-red-600 font-bold">PERMANENT</span> zu löschen.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm font-medium mb-2">Folgende Daten werden unwiderruflich gelöscht:</p>
                <ul className="text-red-700 text-sm space-y-1">
                  <li>• Alle Kundendaten</li>
                  <li>• Alle Termine</li>
                  <li>• Alle Dateien</li>
                  <li>• Zahlungshistorie</li>
                  <li>• Chat-Verläufe</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Geben Sie <span className="font-bold text-red-600">LÖSCHEN</span> ein, um zu bestätigen:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="LÖSCHEN"
                  className="font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              disabled={deleteConfirmText !== 'LÖSCHEN'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Permanent löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Customer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Neuen Kunden anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-barlow font-bold text-sm">Vorname *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="mt-1"
                  data-testid="create-firstname"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Nachname *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="mt-1"
                  data-testid="create-lastname"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-barlow font-bold text-sm">E-Mail *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  data-testid="create-email"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Telefon *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1"
                  data-testid="create-phone"
                />
              </div>
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Straße *</Label>
              <AddressAutocomplete
                value={formData.street}
                onChange={(val) => setFormData({ ...formData, street: val })}
                onAddressSelect={(addr) => {
                  setFormData({
                    ...formData,
                    street: addr.street,
                    postal_code: addr.postcode || formData.postal_code,
                    city: addr.city || formData.city
                  });
                }}
                placeholder="Straße und Hausnummer eingeben..."
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-barlow font-bold text-sm">PLZ *</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="mt-1"
                  data-testid="create-plz"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Ort *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1"
                  data-testid="create-city"
                />
              </div>
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Passwort (optional)</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1"
                placeholder="Wird automatisch generiert wenn leer"
                data-testid="create-password"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Geburtstag (Tag & Monat)</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-1.5">Der Kunde erhält jedes Jahr einen exklusiven Geburtstagsrabatt per E-Mail.</p>
              <div className="flex gap-2">
                <select
                  value={formData.birthday ? formData.birthday.split('-')[1] : ''}
                  onChange={(e) => {
                    const day = formData.birthday ? formData.birthday.split('-')[0] : '';
                    setFormData({ ...formData, birthday: day && e.target.value ? `${day}-${e.target.value}` : '' });
                  }}
                  className="flex-1 h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
                  data-testid="create-birthday-month"
                >
                  <option value="">Monat</option>
                  {['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                    <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>
                  ))}
                </select>
                <select
                  value={formData.birthday ? formData.birthday.split('-')[0] : ''}
                  onChange={(e) => {
                    const month = formData.birthday ? formData.birthday.split('-')[1] : '';
                    setFormData({ ...formData, birthday: e.target.value && month ? `${e.target.value}-${month}` : '' });
                  }}
                  className="w-20 h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
                  data-testid="create-birthday-day"
                >
                  <option value="">Tag</option>
                  {Array.from({length: 31}, (_, i) => i+1).map(d => (
                    <option key={d} value={String(d).padStart(2,'0')}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleCreateCustomer}
              disabled={!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.street || !formData.postal_code || !formData.city}
              data-testid="create-submit"
            >
              Kunde anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Kundendaten bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-barlow font-bold text-sm">Vorname</Label>
                <Input
                  value={editFormData.first_name}
                  onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                  className="mt-1"
                  data-testid="edit-firstname"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Nachname</Label>
                <Input
                  value={editFormData.last_name}
                  onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                  className="mt-1"
                  data-testid="edit-lastname"
                />
              </div>
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">E-Mail</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className="mt-1"
                data-testid="edit-email"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Telefon</Label>
              <Input
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                className="mt-1"
                data-testid="edit-phone"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Straße</Label>
              <AddressAutocomplete
                value={editFormData.street}
                onChange={(val) => setEditFormData({ ...editFormData, street: val })}
                onAddressSelect={(addr) => {
                  setEditFormData({
                    ...editFormData,
                    street: addr.street,
                    postal_code: addr.postcode || editFormData.postal_code,
                    city: addr.city || editFormData.city
                  });
                }}
                placeholder="Straße und Hausnummer eingeben..."
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-barlow font-bold text-sm">PLZ</Label>
                <Input
                  value={editFormData.postal_code}
                  onChange={(e) => setEditFormData({ ...editFormData, postal_code: e.target.value })}
                  className="mt-1"
                  data-testid="edit-plz"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Ort</Label>
                <Input
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  className="mt-1"
                  data-testid="edit-city"
                />
              </div>
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Interne Notiz (nur für Admin sichtbar)</Label>
              <textarea
                value={editFormData.internal_note || ''}
                onChange={(e) => setEditFormData({ ...editFormData, internal_note: e.target.value })}
                className="mt-1 w-full min-h-[80px] p-2 border rounded-md text-sm resize-none"
                placeholder="Interne Notizen zum Kunden..."
                data-testid="edit-internal-note"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Geburtstag (Tag & Monat)</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-1.5">Für den jährlichen Geburtstagsrabatt per E-Mail.</p>
              <div className="flex gap-2">
                <select
                  value={editFormData.birthday ? editFormData.birthday.split('-')[1] : ''}
                  onChange={(e) => {
                    const day = editFormData.birthday ? editFormData.birthday.split('-')[0] : '';
                    setEditFormData({ ...editFormData, birthday: day && e.target.value ? `${day}-${e.target.value}` : '' });
                  }}
                  className="flex-1 h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
                  data-testid="edit-birthday-month"
                >
                  <option value="">Monat</option>
                  {['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                    <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>
                  ))}
                </select>
                <select
                  value={editFormData.birthday ? editFormData.birthday.split('-')[0] : ''}
                  onChange={(e) => {
                    const month = editFormData.birthday ? editFormData.birthday.split('-')[1] : '';
                    setEditFormData({ ...editFormData, birthday: e.target.value && month ? `${e.target.value}-${month}` : '' });
                  }}
                  className="w-20 h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
                  data-testid="edit-birthday-day"
                >
                  <option value="">Tag</option>
                  {Array.from({length: 31}, (_, i) => i+1).map(d => (
                    <option key={d} value={String(d).padStart(2,'0')}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleUpdateCustomer}
              data-testid="edit-submit"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance Dialog */}
      <Dialog open={showBalanceDialog} onOpenChange={setShowBalanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Guthaben bearbeiten</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-manrope text-sm text-gray-500">Aktueller Kontostand</p>
                <p className={`font-barlow font-bold text-2xl ${getBalanceColor(selectedCustomer.balance)}`}>
                  {selectedCustomer.balance.toFixed(2).replace('.', ',')} €
                </p>
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Betrag (+ oder -)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  placeholder="z.B. 50.00 oder -25.00"
                  className="mt-1"
                  data-testid="balance-amount"
                />
              </div>
              <div>
                <Label className="font-barlow font-bold text-sm">Beschreibung</Label>
                <Input
                  value={balanceDescription}
                  onChange={(e) => setBalanceDescription(e.target.value)}
                  placeholder="z.B. Rechnung #123"
                  className="mt-1"
                  data-testid="balance-description"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBalanceDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleUpdateBalance}
              disabled={!balanceAmount}
              data-testid="balance-submit"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Neues Passwort generiert</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="font-manrope text-sm text-gray-600 mb-2">
                  Neues Passwort für <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-4 py-3 rounded border text-lg font-mono font-bold text-[#9B1B30]">
                    {newPassword}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(newPassword)}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Bitte teilen Sie dem Kunden das neue Passwort mit. Der Kunde kann sich damit anmelden 
                und das Passwort in seinen Einstellungen ändern.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button 
              className="btn-primary"
              onClick={() => {
                setShowPasswordDialog(false);
                setNewPassword('');
              }}
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Balance History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-md sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg sm:text-xl">Zahlungshistorie</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2">
            {balanceHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Keine Zahlungen vorhanden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {balanceHistory.map((entry, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="font-manrope text-sm font-medium truncate">
                        {entry.description}
                      </p>
                      <p className="font-manrope text-xs text-gray-500">
                        {format(new Date(entry.updated_at || entry.date), 'dd.MM.yyyy HH:mm', { locale: de })}
                      </p>
                    </div>
                    <div className={`font-barlow font-bold text-sm flex-shrink-0 ${
                      entry.amount >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      <span className="flex items-center gap-1">
                        {entry.amount >= 0 ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {entry.amount >= 0 ? '+' : ''}{entry.amount.toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 border-t">
            <Button 
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowHistoryDialog(false)}
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
